import moment from 'moment';
import { all, call, put, takeLatest } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';

import {
  type DriverNames,
  type Events,
  EVENTS_REQUEST_AMOUNT,
  type EventsWithLatLng,
  EventTypes,
  type GeofenceEvent,
  ReducedEventTypes,
  type RoadSpeedEvent,
  type SpeedEvent,
  type Vehicle,
  type VehicleSpeedEvent,
} from '@cobliteam/common-types';

import { GeocodeRequester, GraphQLDriversRequester, type LatLng, SpeedingRequester } from '@cobliteam/api-lib';
import GeofenceRequester from '@cobliteam/api-lib/src/dashboard/geofence/GeofenceRequesterV3';
import GrahpQLVehicleRequester from '@cobliteam/api-lib/src/dashboard/vehicle/GrahpQLVehicleRequester';

import { Environment } from '@cobliteam/react-environments';

import { loadEventsInfoFailed, loadEventsInfoSucceeded } from '@cobliteam/store/src/EventsInfoTab/actionCreators';
import { EventsActionNames, type LoadEventsInfoRequest } from '@cobliteam/store/src/EventsInfoTab/actions';

const env = Environment.getInstance().getEnvironment();

const geocodeRequester = new GeocodeRequester({ host: env.herbieApiHost });
const speedingRequester = new SpeedingRequester({ host: env.herbieApiHost });
const geofenceV3Requester = new GeofenceRequester({
  host: env.geofenceApiHost,
});
const driversRequester = new GraphQLDriversRequester();
const vehicleRequester = new GrahpQLVehicleRequester();

const getSpeedEventsCommonFields = (driverNames: DriverNames, licensePlatesById: Map<string, string>, speedEvent: SpeedEvent) => ({
  id: uuidv4(),
  date: speedEvent.eventTime,
  deviceId: speedEvent.deviceId,
  licensePlate: licensePlatesById.get(speedEvent.vehicleId) || '',
  driverId: speedEvent.driverId,
  driverName: speedEvent.driverId ? driverNames[speedEvent.driverId] : undefined,
  speedInKmH: speedEvent.medianSpeedInKmh || speedEvent.speedInKmh,
  latitude: speedEvent.location.lat,
  longitude: speedEvent.location.lng,
  durationAboveSpeedLimit: speedEvent.durationAboveInMillis,
});

const geofenceEventToEvent =
  (driverNames: DriverNames, licensePlatesById: Map<string, string>) =>
  (geofenceEvent: GeofenceEvent): EventsWithLatLng => {
    return {
      id: geofenceEvent.id,
      geoType: geofenceEvent.type,
      type: ReducedEventTypes.GEOFENCE,
      geofenceId: geofenceEvent.geofence.id,
      geofenceName: geofenceEvent.geofence.name,
      latitude: geofenceEvent.latitude,
      longitude: geofenceEvent.longitude,
      date: moment(geofenceEvent.time).valueOf(),
      deviceId: geofenceEvent.deviceId,
      licensePlate: licensePlatesById.get(geofenceEvent.vehicleId) || '',
      driverId: geofenceEvent.driverId,
      driverName: geofenceEvent.driverId ? driverNames[geofenceEvent.driverId] : undefined,
    };
  };

const roadSpeedEventToEvent =
  (driverNames: DriverNames, licensePlatesById: Map<string, string>) =>
  (speedEvent: RoadSpeedEvent): EventsWithLatLng => {
    const commonFields = getSpeedEventsCommonFields(driverNames, licensePlatesById, speedEvent);

    return {
      ...commonFields,
      type: ReducedEventTypes.SPEED_LIMIT,
      speedLimitType: EventTypes.SPEED_LIMIT,
      roadSpeedLimitInKmH: speedEvent.speedLimitInKmh,
    };
  };

const vehicleSpeedEventToEvent =
  (driverNames: DriverNames, licensePlatesById: Map<string, string>) =>
  (speedEvent: VehicleSpeedEvent): EventsWithLatLng => {
    const commonFields = getSpeedEventsCommonFields(driverNames, licensePlatesById, speedEvent);
    return {
      ...commonFields,
      type: ReducedEventTypes.SPEED_LIMIT,
      speedLimitType: EventTypes.VEHICLE_SPEED_LIMIT,
      vehicleSpeedLimitInKmH: speedEvent.speedLimitInKmh,
    };
  };

export const getEventsLatLng = (eventsArray: EventsWithLatLng[]): LatLng[] => {
  return eventsArray.map((event) => ({
    lat: event.latitude,
    lng: event.longitude,
  }));
};

export const buildEventsWithAddressArray = (eventsArray: EventsWithLatLng[], addresses: string[]): Events[] => {
  let addressIndex = 0;

  return eventsArray.map((event) => {
    if (event.type === ReducedEventTypes.GEOFENCE || event.type === ReducedEventTypes.SPEED_LIMIT) {
      const eventWithAddress = {
        ...event,

        address: addresses[addressIndex++],
      };

      return eventWithAddress;
    }

    return event;
  });
};

export function* requestEventsInfoSaga(action: LoadEventsInfoRequest) {
  const { startDate, endDate } = action.payload;

  try {
    const driverNames: DriverNames = yield call(driversRequester.getDriverNames);
    const vehicles: Vehicle[] = yield call(vehicleRequester.getVehicles);
    const licensePlatesById = new Map(vehicles.map((v) => [v.id, v.licensePlate || '']));

    const geofenceEvents: GeofenceEvent[] = yield call(geofenceV3Requester.getGeofenceEvents, moment(startDate).toISOString(), moment(endDate).toISOString());

    const geofenceEventsNotification = geofenceEvents.slice(0, EVENTS_REQUEST_AMOUNT).map(geofenceEventToEvent(driverNames, licensePlatesById));

    const roadSpeedEvents: RoadSpeedEvent[] = yield call(speedingRequester.getRoadEvents, startDate, endDate, undefined, undefined, EVENTS_REQUEST_AMOUNT);

    const roadSpeedEventsNotification = roadSpeedEvents.map(roadSpeedEventToEvent(driverNames, licensePlatesById));

    const vehicleSpeedEvents: VehicleSpeedEvent[] = yield call(speedingRequester.getVehicleEvents, startDate, endDate, undefined, undefined, EVENTS_REQUEST_AMOUNT);

    const vehicleSpeedEventsNotification = vehicleSpeedEvents.map(vehicleSpeedEventToEvent(driverNames, licensePlatesById));

    const allEvents = [...geofenceEventsNotification, ...roadSpeedEventsNotification, ...vehicleSpeedEventsNotification];
    const sortedEvents = allEvents.sort((a, b) => (a.date > b.date ? -1 : 1)).slice(0, EVENTS_REQUEST_AMOUNT);

    const eventsLatLng = getEventsLatLng(sortedEvents);
    const addresses: string[] = yield call(geocodeRequester.getAddresses, eventsLatLng);

    yield put(loadEventsInfoSucceeded(buildEventsWithAddressArray(sortedEvents, addresses)));
  } catch (err) {
    yield put(loadEventsInfoFailed());
  }
}

export function* EventsInfoTabSagasListener() {
  yield all([takeLatest(EventsActionNames.LOAD_EVENTS_INFO_REQUEST, requestEventsInfoSaga)]);
}
